import { create } from 'zustand'
import type { PauseAdvisory } from './dayConflicts'

export interface PauseEditing extends PauseAdvisory {
  kind: 'break' | 'tm'
  /** The ScheduleEntry id for a `tm`; absent for a break, which is upserted by technician + day. */
  id?: number
  dayIso: string
  startIso: string
  endIso: string
  anchorRect: DOMRect
  /** Excluded from its own collision check — a block never conflicts with where it already is. */
  excludeKey: string
  /**
   * Why the server refused this, when it refused something the client had cleared.
   *
   * Only reachable through a race: the client applies the same rules before sending. It rides on
   * the editing state rather than sitting beside it so that a drag which never opened the popover
   * can still report back — the failure re-opens it with the reason already in place.
   */
  rejection?: string | null
}

export interface PauseEditState {
  editing: PauseEditing | null
  open: (editing: PauseEditing) => void
  /** Re-runs on every time change in the popover, so the warnings track what is typed. */
  updateRange: (startIso: string, endIso: string, advisory: PauseAdvisory) => void
  close: () => void
}

export const usePauseEditState = create<PauseEditState>((set) => ({
  editing: null,
  open: (editing) => set({ editing }),
  updateRange: (startIso, endIso, advisory) =>
    set((s) =>
      s.editing
        ? // `rejection` is dropped: it described the range that has just been typed over, and
          // leaving it up would blame the new time for the old time's refusal.
          { editing: { ...s.editing, startIso, endIso, ...advisory, rejection: null } }
        : s,
    ),
  close: () => set({ editing: null }),
}))
